import { useMemo } from "react";
import {
  MAX_SUBGRAPH_ID,
  REQUEST_ID,
  STREAMS_PAGE_SIZE,
} from "@sablier/v2-constants";
import { _ } from "@sablier/v2-mixins";
import { Stream, Token } from "@sablier/v2-models";
import { client } from "@sablier/v2-subgraphs";
import { IFilter } from "@sablier/v2-types";
import { useQuery } from "@tanstack/react-query";
import request from "graphql-request";
import { ISearch } from "./types";
import useRequestQuery from "./useRequestStreamListQuery";

interface Props {
  key?: string[];
  filter: IFilter;
  options?: {
    after: string;
    first: number;
  };
  isEnabled?: boolean;
  onSuccess: (result: ISearch) => void;
  onError?: (error: unknown, result: ISearch) => void;
}

export default function useRequestStreamList({
  key = REQUEST_ID.streamList,
  filter,
  onError = _.noop,
  onSuccess = _.noop,
  isEnabled = false,
  options = {
    after: MAX_SUBGRAPH_ID,
    first: STREAMS_PAGE_SIZE,
  },
}: Props) {
  const endpoint = useMemo(
    () => client.getEndpointByChain({ chainId: filter.chainId }) || "",
    [filter],
  );

  const query = useRequestQuery(filter);
  const variables = useMemo(() => {
    const sender = filter.sender?.toLowerCase();
    const recipient = filter.recipient?.toLowerCase();

    return {
      chainId: filter.chainId,
      first: options.first,
      skip: 0,
      streamIds: filter.streamIds?.map((id) => id?.toLowerCase()),
      subgraphId: options.after,
      sender,
      recipient,
    };
  }, [filter, options]);

  const { error, isLoading } = useQuery({
    queryKey: [...key, { unique: variables }],
    queryFn: async () => request(endpoint, query, variables),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: isEnabled,
    retry: false,
    onSuccess: (data) => {
      const after = data?.streams.length
        ? data.streams[data.streams.length - 1].subgraphId
        : MAX_SUBGRAPH_ID;
      const isComplete = (data?.streams.length || 0) < options.first;

      const search: ISearch = {
        filter,
        options: {
          after,
          first: options.first,
          isComplete,
          variables,
        },
        streams: [],
      };

      if (!_.isNilOrEmptyString(data)) {
        data.streams.forEach((item) => {
          const token = new Token(item.asset);
          const stream = new Stream(item, token);
          search.streams.push(stream);
        });
      }

      onSuccess(search);
    },
    onError: (error: unknown) => {
      const search: ISearch = {
        filter,
        options: {
          after: MAX_SUBGRAPH_ID,
          first: options.first,
          error: _.toString(error),
          isComplete: true,
          variables,
        },
        streams: [],
      };

      onError(error, search);
    },
  });

  return useMemo(
    () => ({
      error,
      isLoading: isLoading && isEnabled,
    }),
    [error, isLoading, isEnabled],
  );
}
